<template>
  <main class="bg-white px-4 pt-16 pb-24 sm:px-6 sm:pt-24 lg:px-8 lg:py-32">
    <div class="max-w-3xl mx-auto">
      <div class="max-w-xl">
        <h1 class="text-sm font-semibold uppercase tracking-wide text-indigo-600">
          Lost your tickets?
        </h1>
        <p class="mt-2 text-4xl font-extrabold tracking-tight sm:text-5xl">
          Recover Tickets
        </p>

        <div class="mt-4">
          <p class="mt-2 text-md mb-4">
            To have your tickets re-sent to you, please enter your email address
            below and click 'find tickets'. Any orders found in our system
            matching your email address will be re-sent to you.
          </p>
          <label for="email" class="block text-sm font-medium text-gray-700">Enter your email address</label>
          <div class="mt-1 flex rounded-md shadow-sm">
            <div class="relative flex items-stretch flex-grow focus-within:z-10">
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <AtSymbolIcon class="h-5 w-5 text-gray-400" aria-hidden="true" />
              </div>
              <input
                id="email"
                v-model="email"
                type="email"
                name="email"
                class="focus:ring-indigo-500 focus:border-indigo-500 block w-full rounded-none rounded-l-md pl-10 sm:text-sm border-gray-300"
                placeholder="your@email.com"
                @keyup="isValidEmail"
              >
            </div>
            <button
              type="button"
              class="-ml-px relative inline-flex items-center space-x-2 px-4 py-2 border border-gray-300 text-sm font-medium rounded-r-md text-gray-700 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
              :class="{ 'disable-button': !email || emailError }"
              @click="recoverTickets"
            >
              <MagnifyingGlassIcon class="h-5 w-5 text-gray-400" aria-hidden="true" />
              <span>Find Tickets</span>
            </button>
          </div>
          <div v-if="email && emailError" class="text-red-600 text-xs mt-1 ml-1">
            Invalid email
          </div>
          <div
            v-if="alertMessage"
            class="rounded-md p-4 mt-4"
            :class="{
              'bg-green-50': ticketRecoverySuccess,
              'bg-red-50': !ticketRecoverySuccess,
            }"
          >
            <div class="flex">
              <div class="flex-shrink-0">
                <CheckCircleIcon v-if="ticketRecoverySuccess" class="h-5 w-5 text-green-400" aria-hidden="true" />
                <XCircleIcon v-if="!ticketRecoverySuccess" class="h-5 w-5 text-red-400" aria-hidden="true" />
              </div>
              <div class="ml-3">
                <p
                  class="text-sm font-medium"
                  :class="{
                    'text-green-800': ticketRecoverySuccess,
                    'text-red-800': !ticketRecoverySuccess,
                  }"
                >
                  {{ alertMessage }}
                </p>
              </div>
              <div class="ml-auto pl-3">
                <div class="-mx-1.5 -my-1.5">
                  <button
                    type="button"
                    class="inline-flex rounded-md p-1.5 focus:outline-none focus:ring-2 focus:ring-offset-2"
                    :class="{
                      'bg-green-50 text-green-500 hover:bg-green-100  focus:ring-offset-green-50 focus:ring-green-600':
                        ticketRecoverySuccess,
                      'bg-red-50 text-red-500 hover:bg-red-100  focus:ring-offset-red-50 focus:ring-red-600':
                        !ticketRecoverySuccess,
                    }"
                  >
                    <span class="sr-only">Dismiss</span>
                    <XMarkIcon class="h-5 w-5" aria-hidden="true" @click="alertMessage = ''" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script setup lang="ts">
import {
  AtSymbolIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/vue/24/outline'

const config = useRuntimeConfig()

definePageMeta({
  layout: 'order'
})

const route = useRoute()
const { tenant } = route?.params
const alertMessage = ref('')
const ticketRecoverySuccess = ref(false)

// Validate email address
const emailError = ref(false)
const email = ref('')
function isValidEmail () {
  const emailPattern =
    /^(?=[a-zA-Z0-9@._%+-]{6,254}$)[a-zA-Z0-9._%+-]{1,64}@(?:[a-zA-Z0-9-]{1,63}\.){1,8}[a-zA-Z]{2,63}$/
  emailError.value = !emailPattern.test(email.value)
  return emailPattern.test(email.value) || 'Invalid email'
}

const recoverTickets = async () => {
  try {
    const { data } = await useFetch(
      `${config.public.WETIX_SERVICE_API_BASE_URL}/v1/tickets/recovery`,
      {
        method: 'POST',
        body: {
          email: email.value,
          tenantId: tenant
        }
      }
    ) as any
    // console.log(data.value);
    alertMessage.value = data.value.message
    ticketRecoverySuccess.value = data.value.success
    email.value = ''
  } catch (error) {
    console.log(error)
  }
}
</script>

<style>
.disable-button {
  pointer-events: none;
}
</style>
